"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { BarChart } from "@/components/tools/stats-distributions/charts";
import {
  DistributionTable,
  ProbabilitySummary,
} from "@/components/tools/stats-distributions/discrete-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { normalCdf } from "@/lib/stats/distributions";
import { barGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import {
  binomialModel,
  type DiscreteModel,
  discreteProbabilities,
  distributionTable,
} from "@/lib/tool-kit/stats-distributions/discrete";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";

/** English strings. A translated content file overrides these through `ui`. */
export const BINOMIAL_DISTRIBUTION_CALCULATOR_STRINGS = {
  nLabel: "Number of trials (n)",
  pLabel: "Probability of success on each trial (p)",
  kLabel: "Number of successes (k)",
  k2Label: "Upper end of a range (optional)",
  k2Hint: "Adds P(k ≤ X ≤ this value) to the results.",
  results: "Results",
  probability: "Probability",
  meaning: "Meaning",
  value: "Value",
  equal: "exactly {k}",
  atMost: "at most {k}",
  lessThan: "fewer than {k}",
  atLeast: "at least {k}",
  greaterThan: "more than {k}",
  between: "from {a} to {b}, inclusive",
  mean: "Mean (np)",
  variance: "Variance (np(1 − p))",
  sd: "Standard deviation",
  normalOk:
    "np = {np} and n(1 − p) = {nq} are both at least 10, so the normal approximation N({mean}, {sd}²) is reasonable. With a continuity correction it gives P(X ≤ {k}) ≈ {approx}, against the exact {exact}.",
  normalPoor:
    "np = {np} or n(1 − p) = {nq} is below 10, so a normal approximation would be unreliable here — use the exact values above.",
  chartHeading: "Probability distribution",
  chartLabel:
    "Bar chart of P(X = k) for k from 0 to {n}, with the bars your question covers drawn darker",
  tableCaption: "Binomial probabilities for n = {n}, p = {p}",
  tableLimit: "The table and chart are drawn for n up to 200.",
  enterN: "Enter the number of trials as a whole number of at least 1.",
  enterP: "Enter p between 0 and 1, such as 0.5.",
  enterK: "Enter k as a whole number between 0 and n.",
} as const;

type Strings = typeof BINOMIAL_DISTRIBUTION_CALCULATOR_STRINGS;

const MAX_N = 10_000_000;
const MAX_CHART_N = 200;

interface Inputs {
  n: number;
  p: number;
  k: number;
  k2: number | null;
}

function readInputs(
  nText: string,
  pText: string,
  kText: string,
  k2Text: string
): Inputs | keyof Strings {
  const n = parseIntegerInput(nText);
  if (n === null || n < 1 || n > MAX_N) {
    return "enterN";
  }
  const p = parseNumberInput(pText);
  if (p === null || p < 0 || p > 1) {
    return "enterP";
  }
  const k = parseIntegerInput(kText);
  const k2 = k2Text.trim() === "" ? null : parseIntegerInput(k2Text);
  const inRange = (value: number | null) =>
    value !== null && value >= 0 && value <= n;
  if (!inRange(k) || (k2Text.trim() !== "" && !inRange(k2))) {
    return "enterK";
  }
  return { n, p, k: k as number, k2 };
}

function NormalNote({
  inputs,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const np = inputs.n * inputs.p;
  const nq = inputs.n * (1 - inputs.p);
  const sd = Math.sqrt(model.variance);
  const four = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  if (np >= 10 && nq >= 10) {
    const approx = normalCdf(inputs.k + 0.5, model.mean, sd);
    return (
      <Notice>
        {t(strings.normalOk, {
          np: four(np),
          nq: four(nq),
          mean: four(model.mean),
          sd: four(sd),
          k: fmt(inputs.k),
          approx: four(approx),
          exact: four(model.cdf(inputs.k)),
        })}
      </Notice>
    );
  }
  return (
    <Notice>{t(strings.normalPoor, { np: four(np), nq: four(nq) })}</Notice>
  );
}

function Distribution({
  inputs,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { n, p, k, k2 } = inputs;
  const rows = useMemo(() => distributionTable(model, 0, n), [model, n]);
  const geometry = useMemo(
    () => barGeometry(rows.map((row) => ({ k: row.k, p: row.pmf }))),
    [rows]
  );
  const low = k2 === null ? k : Math.min(k, k2);
  const high = k2 === null ? k : Math.max(k, k2);
  const highlighted = (value: number) => value >= low && value <= high;
  return (
    <>
      <div className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
        <BarChart
          format={(value) => fmt(value)}
          geometry={geometry}
          highlighted={highlighted}
          label={t(strings.chartLabel, { n: fmt(n) })}
        />
      </div>
      <DistributionTable
        caption={t(strings.tableCaption, {
          n: fmt(n),
          p: fmt(p, { maximumFractionDigits: 6 }),
        })}
        fmt={fmt}
        highlighted={highlighted}
        rows={rows}
      />
    </>
  );
}

export default function BinomialDistributionCalculatorWidget() {
  const strings = useToolUi(BINOMIAL_DISTRIBUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [nText, setNText] = useState("20");
  const [pText, setPText] = useState("0.5");
  const [kText, setKText] = useState("12");
  const [k2Text, setK2Text] = useState("");

  const inputs = readInputs(nText, pText, kText, k2Text);
  const n = typeof inputs === "object" ? inputs.n : 1;
  const p = typeof inputs === "object" ? inputs.p : 0;
  const model = useMemo(() => binomialModel(n, p), [n, p]);

  return (
    <>
      <section className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="bd-n"
          label={strings.nLabel}
          numeric
          onChange={setNText}
          value={nText}
        />
        <TextField
          id="bd-p"
          label={strings.pLabel}
          numeric
          onChange={setPText}
          value={pText}
        />
        <TextField
          id="bd-k"
          label={strings.kLabel}
          numeric
          onChange={setKText}
          value={kText}
        />
        <TextField
          hint={strings.k2Hint}
          id="bd-k2"
          label={strings.k2Label}
          numeric
          onChange={setK2Text}
          value={k2Text}
        />
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof inputs === "string" ? (
          <Notice>{strings[inputs]}</Notice>
        ) : (
          <>
            <ProbabilitySummary
              fmt={fmt}
              k={inputs.k}
              labels={strings}
              probabilities={discreteProbabilities(model, inputs.k, inputs.k2)}
            />
            <StatGrid>
              <StatCard
                label={strings.mean}
                value={fmt(model.mean, { maximumFractionDigits: 4 })}
              />
              <StatCard
                label={strings.variance}
                value={fmt(model.variance, { maximumFractionDigits: 4 })}
              />
              <StatCard
                label={strings.sd}
                value={fmt(Math.sqrt(model.variance), {
                  maximumFractionDigits: 4,
                })}
              />
            </StatGrid>
            <NormalNote
              fmt={fmt}
              inputs={inputs}
              model={model}
              strings={strings}
            />
            {inputs.n <= MAX_CHART_N ? (
              <Distribution
                fmt={fmt}
                inputs={inputs}
                model={model}
                strings={strings}
              />
            ) : (
              <Notice>{strings.tableLimit}</Notice>
            )}
          </>
        )}
      </section>
    </>
  );
}
